import React from "react";

import {
  AddRounded,
  ContentCopyRounded,
  DeleteRounded,
  LockOpenRounded,
  LockRounded,
  MenuRounded,
  OpenInNewRounded,
  PersonRounded,
  StraightenRounded,
  VpnKeyRounded,
} from "@mui/icons-material";
import { Box, Skeleton } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import useAuth from "../../../config/context/authContext";
import useFirebase from "../../../config/context/firebaseContext";
import useMainState from "../../../config/context/mainStateContext";
import useModal from "../../../config/context/modalContext";
import { FileService } from "../../../config/services/FileService";
import { GreyPallet } from "../../../config/styles";
import { Dev } from "../../../config/util/consoleUtil";
import { UtilComponents } from "../../../config/util/utilComponents";

import MainText from "../../../components/MainText";
import PageHeader from "../../../components/PageHeader";
import ModalFileCreate from "./components/ModalFileCreate";

import UtilFunctions from "../../../config/util/utilFunctions";
import { CustomButton } from "./styles";

export default function AdminFilePage() {
  const navigate = useNavigate();

  const { setShowSideMenuDrawer, openLink } = useMainState();
  const { currentUserInfo } = useAuth();

  const fileList = FileService.List();

  const { functions } = useFirebase();
  const { pushAlert } = useModal();

  const [showModalCreate, setShowModalCreate] = React.useState(false);

  const handleFetchFiles = async () => {
    fileList
      .runRequest()
      .catch((err) =>
        pushAlert({ type: "error", message: "Erro ao buscar arquivos. " + err.message })
      );
  };

  const handleDeleteFile = async () => {
    try {
      pushAlert({
        isConfirm: true,
        type: "delete",
        onConfirm: async () => {
          try {
            // TODO
            // await SpottDelete.runRequest(params?.postId || "");

            pushAlert({
              type: "success",
              message: "Arquivo removido",
            });
          } catch (error) {
            pushAlert({
              type: "error",
              message: "Erro ao atualizar arquivos. " + (error as Error).message,
            });
          }

          handleFetchFiles();
        },
        message: "Você deseja realmente remover esse arquivo?",
      });
    } catch (error) {
      console.log(...new Dev("handleDeleteFile", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao remover arquivo",
      });
    }
  };

  //

  React.useEffect(() => {
    handleFetchFiles();
  }, []);

  return (
    <Box
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
      pt={"30px"}
      pb={"45px"}
    >
      <Box width={"90%"}>
        <PageHeader
          title="Arquivos"
          leftIcon={<MenuRounded />}
          onLeftAction={() => setShowSideMenuDrawer(true)}
          rightIcon={<AddRounded />}
          onRightAction={() => setShowModalCreate(true)}
        />

        {showModalCreate && (
          <ModalFileCreate
            onClose={(reload) => {
              setShowModalCreate(false);
              if (reload) handleFetchFiles();
            }}
          />
        )}

        <Box mt={4}>
          {fileList.isLoading ? (
            <>
              <Skeleton
                animation="wave"
                variant="rounded"
                width={"100%"}
                height={153}
                sx={{ borderRadius: "15px", mb: 2 }}
              />
              <Skeleton
                animation="wave"
                variant="rounded"
                width={"100%"}
                height={153}
                sx={{ borderRadius: "15px" }}
              />
            </>
          ) : (
            fileList.items?.map((item) => (
              <Box
                key={item.id}
                sx={{
                  backgroundColor: GreyPallet.Light,
                  borderRadius: "15px",
                  p: 2,
                  mb: 2,
                }}
              >
                <Box
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <MainText size="small" color="regular">
                    {DateTime.fromMillis(item?.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
                  </MainText>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <StraightenRounded sx={{ color: GreyPallet.Regular, fontSize: 16 }} />
                    <MainText size="small" color="regular">
                      {UtilFunctions.formatBytes(item.size, 1)}
                    </MainText>

                    {item.public ? (
                      <LockOpenRounded fontSize="small" sx={{ marginLeft: 1 }} />
                    ) : (
                      <LockRounded fontSize="small" sx={{ marginLeft: 1 }} />
                    )}
                  </Box>
                </Box>
                <MainText size="body" color="black">
                  {item.name}
                </MainText>
                <MainText size="small" color="black">
                  {item.originalName}
                </MainText>
                <MainText size="small" color="regular">
                  {item.description}
                </MainText>
                <Box mt={2} display={"flex"} justifyContent="space-around" alignItems={"center"}>
                  <CustomButton onClick={() => openLink(item.resource)}>
                    <OpenInNewRounded />
                  </CustomButton>

                  <CustomButton
                    onClick={() =>
                      UtilComponents.copyToClipboard(
                        pushAlert,
                        item.resource,
                        "URL Copiada para área de transferência!"
                      )
                    }
                  >
                    <ContentCopyRounded />
                  </CustomButton>

                  <CustomButton
                    onClick={() =>
                      UtilComponents.copyToClipboard(
                        pushAlert,
                        item.ref,
                        "Referência Copiada para área de transferência!"
                      )
                    }
                  >
                    <VpnKeyRounded />
                  </CustomButton>

                  <CustomButton
                    disabled={!!!item.owner}
                    onClick={() => navigate("/user/" + item.owner)}
                  >
                    <PersonRounded />
                  </CustomButton>

                  <CustomButton onClick={handleDeleteFile}>
                    <DeleteRounded />
                  </CustomButton>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}
